import { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { writeData } from "../../../api";
import { insertPageBlock } from "../../../api/page";
import { LogStreamContext } from "../../../components/page/block/logStream";
import { ActionType, AppDispatch, RootState } from "../../../reducer";
import { LogStream } from "../../../types/logStream";
import { LogStreamBlock } from "../../../types/notebook/note/block/logStream";
import { useAppIdMap } from "../../app";
import { useSelectedNotebookPageIdMap } from "../../page";
import { useInsertBlock } from "../block";
import { AddLogStreamsAction } from "../../../reducer/logStream";
import { getLogStream } from "../../../api/logStream";
import { debounce } from "lodash";

export const useLogStream = (logStreamId: string | undefined) => {
  const logStream = useSelector((state: RootState) => {
    if (!logStreamId) return;
    const logStreamMap = state.ui.logStreamMap;
    if (!(logStreamId in logStreamMap)) return;
    return logStreamMap[logStreamId];
  });
  return { logStream };
};

export const useLogStreamContext = () => {
  const ctxValue = useContext(LogStreamContext);
  if (!ctxValue) throw new Error("Context must be defined");
  return ctxValue;
};

export const useAddLogsStreams = () => {
  const dispatch = useDispatch<AppDispatch>();
  return (logStreams: LogStream[]) => {
    const action: AddLogStreamsAction = {
      type: ActionType.ADD_LOG_STREAMS,
      logStreams,
    };
    dispatch(action as any);
  };
};

export const useInsertLogStreamBlock = () => {
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  const { appId } = useAppIdMap();
  const insertBlock = useInsertBlock();
  const addLogStreams = useAddLogsStreams();

  return async (config: { blockIndex: number }) => {
    const { blockIndex } = config;
    const logStream: LogStream = {
      "app-id": appId!,
      "log-stream-id": uuidv4(),
      type: "omega/query-omega/log-stream/LogStream",
      name: "New Log Stream",
    };
    const block: LogStreamBlock = {
      "block-id": uuidv4(),
      "app-id": appId!,
      "log-stream-id": logStream["log-stream-id"],
      "page-id": selectedNotebookPageId!,
      type: "omega/query-omega/page/block/log-stream/LogStreamBlock",
    };
    addLogStreams([logStream]);
    insertBlock({ block, blockIndex });
    await writeData(logStream);
    await insertPageBlock({ block, "block-index": blockIndex });
    return block;
  };
};

export const useFetchLogStreamListener = (logStreamId: string) => {
  const addLogsStreams = useAddLogsStreams();
  useEffect(() => {
    getLogStream({ "log-stream-id": logStreamId }).then((logStream) => {
      addLogsStreams([logStream]);
    });
  }, [logStreamId]);
};

export const useLogStreamNameEditor = (logStreamId: string | undefined) => {
  const { logStream } = useLogStream(logStreamId);
  const logStreamNameState = logStream?.name;
  const [nameLocal, setNameLocal] = useState(logStreamNameState ?? "");
  const addLogStreams = useAddLogsStreams();

  useEffect(() => {
    setNameLocal(logStreamNameState ?? "");
  }, [logStreamNameState]);

  const writeBackend = useMemo(
    () =>
      debounce((name: string) => {
        if (!logStream) return;
        const newLogStream: LogStream = { ...logStream, name };
        addLogStreams([newLogStream]);
        writeData(newLogStream);
      }, 500),
    [logStreamId],
  );

  const setName = (name: string) => {
    setNameLocal(name);
    writeBackend(name);
  };

  return { name: nameLocal, setName };
};
